// api/encounterItemApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { serverApi, handleApiResponse } from "@core/api/core"
import { EncounterItemSerializer } from "@/types/encounterItem"
import { InvoiceBaseSerializer } from "@/types/invoice"
import { CommonTResponse } from "@/types/common"
import { JaboClaimUninsuredItemSerializer } from "@/types/uninsured"

interface EncounterItemListWithInvoiceResponse {
  message: string
  results?: EncounterItemSerializer[]
  invoice?: InvoiceBaseSerializer
}

interface JaboClaimUninsuredAvailableResponse {
  message: string
  results?: JaboClaimUninsuredItemSerializer[]
}

export interface EncounterPackageUsageRequestPayload {
  packageUuid: string
  usedCount: number
  note?: string
  encounterItemUuid?: string
  encounterItemOrder?: number
  encounterItemName?: string
  encounterItemCode?: string
}

export interface PatchEncounterItemsPayload {
  encounterItems: EncounterItemSerializer[]
  packageUsages?: EncounterPackageUsageRequestPayload[]
  forceDeletePackagePurchaseItemUuids?: string[]
}

export const patchEncounterItems = async (
  encounterUuid: string,
  payload: EncounterItemSerializer[] | PatchEncounterItemsPayload
): Promise<[EncounterItemSerializer[], InvoiceBaseSerializer]> => {
  const requestUrl = `/encounter/encounter_item/patch_items/?encounter_uuid=${encounterUuid}`
  const response = await serverApi.patch<CommonTResponse<EncounterItemSerializer>>(requestUrl, payload)

  // 서버 응답: {message: 'success', result: [...], invoice: {...}}
  const data = response.data
  return [data.results, data.invoice]
}

export const patchEncounterItem = async (
  encounterItemUuid: string,
  item: Partial<EncounterItemSerializer>
): Promise<[EncounterItemSerializer, InvoiceBaseSerializer]> => {
  const requestUrl = `/encounter/encounter_item/patch_item/`
  const response = await serverApi.patch<CommonTResponse<EncounterItemSerializer>>(requestUrl, {
    ...item,
    encounterItemUuid,
  })

  return [handleApiResponse<EncounterItemSerializer>(response), response.data.invoice as InvoiceBaseSerializer]
}

export const applyEncounterItemDiscount = async (
  encounterItemUuid: string,
  item: Pick<EncounterItemSerializer, "encounterItemPrice" | "encounterItemOriginalPrice" | "encounterItemAmount" | "encounterItemSpecificDetail">
): Promise<[EncounterItemSerializer, InvoiceBaseSerializer]> => {
  const requestUrl = `/encounter/encounter_item/apply_discount/`
  const response = await serverApi.patch<CommonTResponse<EncounterItemSerializer>>(requestUrl, {
    ...item,
    encounterItemUuid,
  })

  return [handleApiResponse<EncounterItemSerializer>(response), response.data.invoice as InvoiceBaseSerializer]
}

export const getEncounterItems = async (encounterUuid: string): Promise<[EncounterItemSerializer[], InvoiceBaseSerializer]> => {
  const requestUrl = `/encounter/encounter_item/?encounter_uuid=${encounterUuid}`
  const response = await serverApi.get<CommonTResponse<EncounterItemSerializer>>(requestUrl)

  // 서버 응답: {message: 'success', results: [...], invoice: {...}}
  const responseData = response.data

  // results가 없으면 빈 배열로 처리
  const items = Array.isArray(responseData.results) ? responseData.results : []
  const invoice = responseData.invoice || ({} as InvoiceBaseSerializer)

  return [items, invoice]
}

export const fetchEncounterItems = createAsyncThunk<
  { items: EncounterItemSerializer[]; invoice?: InvoiceBaseSerializer },
  { encounterUuid: string },
  { rejectValue: string }
>("encounterItem/fetchEncounterItems", async ({ encounterUuid }, { rejectWithValue }) => {
  try {
    const requestUrl = `/encounter/encounter_item/?encounter_uuid=${encounterUuid}`
    const response = await serverApi.get<CommonTResponse<EncounterItemSerializer>>(requestUrl)

    // 서버 응답: {message: 'success', results: [...], invoice: {...}}
    const responseData = response.data
    return {
      items: responseData.results,
      invoice: responseData.invoice,
    }
  } catch (err: any) {
    return rejectWithValue(err.message || "EncounterItems 불러오는 중 오류가 발생했습니다.")
  }
})

export const calculateEncounterItem = async (item: EncounterItemSerializer): Promise<EncounterItemSerializer[]> => {
  const requestUrl = `/encounter/encounter_item/calculate/`
  const response = await serverApi.post<CommonTResponse<EncounterItemSerializer[]>>(requestUrl, item)

  const responseData = handleApiResponse<EncounterItemSerializer[]>(response)

  // 서버가 배열을 반환하므로 그대로 사용
  if (Array.isArray(responseData)) {
    return responseData
  }

  // 혹시 단일 객체가 올 경우 배열로 감싸기
  return [responseData]
}

export const calculateEncounterItemAll = async (items: EncounterItemSerializer[]): Promise<EncounterItemSerializer[]> => {
  const requestUrl = `/encounter/encounter_item/calculate_all/`
  const response = await serverApi.post<CommonTResponse<EncounterItemSerializer[]>>(requestUrl, items)
  const responseData = handleApiResponse<EncounterItemSerializer[]>(response)

  return responseData
}

export const addUninsuredEncounterItemFromHistory = async (payload: {
  encounterUuid: string
  encounterItemName: string
  encounterItemCode: string
  encounterItemPrice: number
  encounterItemDailyAmount: number
  encounterItemDays?: number
  encounterItemAmount: number
  encounterItemIsTax?: boolean
}): Promise<[EncounterItemSerializer[], InvoiceBaseSerializer]> => {
  const requestUrl = `/encounter/encounter_item/add_uninsured_item_from_history/`
  const response = await serverApi.post<CommonTResponse<EncounterItemSerializer[]>>(requestUrl, {
    encounter_uuid: payload.encounterUuid,
    encounter_item_name: payload.encounterItemName,
    encounter_item_code: payload.encounterItemCode,
    encounter_item_price: payload.encounterItemPrice,
    encounter_item_daily_amount: payload.encounterItemDailyAmount,
    encounter_item_days: payload.encounterItemDays ?? 1,
    encounter_item_amount: payload.encounterItemAmount,
    encounter_item_is_tax: payload.encounterItemIsTax ?? false,
  })

  const data = response.data
  return [Array.isArray(data.results) ? data.results : [], data.invoice as InvoiceBaseSerializer]
}

export const deleteUninsuredEncounterItemFromHistory = async (payload: {
  encounterUuid: string
  encounterItemUuid: string
}): Promise<[EncounterItemSerializer[], InvoiceBaseSerializer]> => {
  const requestUrl = `/encounter/encounter_item/delete_uninsured_item_from_history/`
  const response = await serverApi.post<CommonTResponse<EncounterItemSerializer[]>>(requestUrl, {
    encounter_uuid: payload.encounterUuid,
    encounter_item_uuid: payload.encounterItemUuid,
  })

  const data = response.data
  return [Array.isArray(data.results) ? data.results : [], data.invoice as InvoiceBaseSerializer]
}

export const getJaboClaimUninsuredAvailable = async (): Promise<JaboClaimUninsuredItemSerializer[]> => {
  const requestUrl = `/encounter/encounter_item/jabo-claim-uninsured-available/`
  const response = await serverApi.get<JaboClaimUninsuredAvailableResponse>(requestUrl)
  return Array.isArray(response.data.results) ? response.data.results : []
}

